// src/components/admin/Authors.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Users,
  Search,
  Filter,
  Eye,
  ChevronLeft,
  ChevronRight,
  User,
  X,
  Mail,
  MapPin,
  Calendar,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  Download,
  RefreshCw,
  Grid,
  List,
  UserCheck,
  UserCog,
  Trash2,
  Shield,
  AlertTriangle,
  Check,
  Archive,
  Clock
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from '../../utils/axios';
import { showToast } from '../../utils/toast';
import Unauthorized from './UnauthorizedAccess';
import { EIC_ADMIN_ROLES, ALLOWED_ROLES } from './utils/AllowedRoles';

const Authors = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewType, setViewType] = useState(() => {
    const saved = localStorage.getItem('authors_view');
    return saved || 'table';
  });
   
// Check if user has required editorial level
  const isAuthorized = user && (EIC_ADMIN_ROLES.includes(user.editorial_level || user.editorialLevel) || ALLOWED_ROLES.includes(user.editorial_level || user.editorialLevel));

  if (!isAuthorized) {
    return (
      <Unauthorized
        message="You need editor-in-chief or admin privileges to access this feature."
      />
    );
  }
   
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [selectedAuthor, setSelectedAuthor] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showMigrateModal, setShowMigrateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [verifyReason, setVerifyReason] = useState('');
  const [migrateRole, setMigrateRole] = useState('editor');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  
  // Data states
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch authors data
  useEffect(() => {
    const fetchAuthors = async () => {
      setLoading(true);
      try {
        const response = await axios.get('/editors/authorsList', {
          params: {
            page: currentPage,
            limit: itemsPerPage,
            search: debouncedSearchTerm,
            status: filterStatus
          }
        });

        if (response.data.status === 'success') {
          setAuthors(response.data.authors);
          setTotalItems(response.data.total);
          setTotalPages(response.data.totalPages);
        } else {
          showToast(response.data.message || response.message || 'Failed to load authors', 'error');
        }
      } catch (error) {
        console.error('Error fetching authors:', error);
        showToast('Failed to load authors. Please try again.', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchAuthors();
  }, [currentPage, itemsPerPage, debouncedSearchTerm, filterStatus]);

  // Save view type to localStorage
  useEffect(() => {
    localStorage.setItem('authors_view', viewType);
  }, [viewType]);

  const getStatusBadge = (status) => {
    const badges = {
      verified: { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
      unverified: { color: 'bg-orange-100 text-orange-800 border-orange-200', icon: AlertCircle },
      suspended: { color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle }
    };
    return badges[status] || badges.unverified;
  };

  const getRoleBadge = (role) => {
    const badges = {
      author: { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: User },
      reviewer: { color: 'bg-purple-100 text-purple-800 border-purple-200', icon: UserCheck },
      editor: { color: 'bg-green-100 text-green-800 border-green-200', icon: Shield }
    };
    return badges[role] || badges.author;
  };

  // Stats using the new data
  const stats = {
    total: totalItems,
    verified: authors.filter(a => a.status === 'verified').length,
    unverified: authors.filter(a => a.status === 'unverified').length,
    totalSubmissions: authors.reduce((sum, a) => sum + (a.submissions || 0), 0),
    totalActiveSubmissions: authors.reduce((sum, a) => sum + (a.activeSubmissions || 0), 0),
    totalArchivedSubmissions: authors.reduce((sum, a) => sum + (a.archivedSubmissions || 0), 0),
    totalReviews: authors.reduce((sum, a) => sum + (a.reviews || 0), 0)
  };

  const handleViewAuthor = (author) => {
    setSelectedAuthor(author);
    setShowViewModal(true);
  };

  const handleVerifyClick = (author) => {
    setSelectedAuthor(author);
    setVerifyReason('');
    setShowVerifyModal(true);
  };

  const handleVerifyConfirm = async () => {
    setActionLoading(true);
    
    try {
      const response = await axios.post('/editors/accounts/verifyUser', {
        id: selectedAuthor.email
      });

      if (response.data.success) {
        // Update local state
        setAuthors(prev => prev.map(a => 
          a.email === selectedAuthor.email 
            ? { ...a, status: 'verified' } 
            : a
        ));
        
        showToast('Author account verified successfully', 'success');
        
        setShowVerifyModal(false);
        setSelectedAuthor(null);
        setVerifyReason('');
      } else {
        showToast(response.data.error || 'Failed to verify account', 'error');
      }
    } catch (error) {
      console.error('Error verifying author:', error);
      showToast('Failed to verify account. Please try again.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMigrateClick = (author) => {
    setSelectedAuthor(author);
    setMigrateRole('editor');
    setShowMigrateModal(true);
  };

  const handleMigrateConfirm = async () => {
    setActionLoading(true);
    
    try {
      const response = await axios.post('/editors/accounts/migrateAuthor', {
        id: selectedAuthor.email
      });

      if (response.data.success) {
        // Update local state
        setAuthors(prev => prev.map(a => 
          a.email === selectedAuthor.email 
            ? { ...a, role: migrateRole, status: 'verified' } 
            : a
        ));
        
        showToast(`Author migrated to ${migrateRole} successfully`, 'success');
        
        setShowMigrateModal(false);
        setSelectedAuthor(null);
        setMigrateRole('editor');
      } else {
        showToast(response.data.error || 'Failed to migrate account', 'error');
      }
    } catch (error) {
      console.error('Error migrating author:', error);
      showToast('Failed to migrate account. Please try again.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteClick = (author) => {
    setSelectedAuthor(author);
    setDeleteConfirmText('');
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmText !== selectedAuthor.email) {
      showToast('Email confirmation does not match', 'error');
      return;
    }

    setActionLoading(true);
    
    try {
      const response = await axios.post('/editors/accounts/deleteAuthor', {
        id: selectedAuthor.email
      });

      if (response.data.success) {
        // Remove from local state
        setAuthors(prev => prev.filter(a => a.email !== selectedAuthor.email));
        
        showToast('Author account deleted successfully', 'success');
        
        setShowDeleteModal(false);
        setSelectedAuthor(null);
        setDeleteConfirmText('');
        
        // Refresh the list or update total
        setTotalItems(prev => prev - 1);
      } else {
        showToast(response.data.error || 'Failed to delete account', 'error');
      }
    } catch (error) {
      console.error('Error deleting author:', error);
      showToast('Failed to delete account. Please try again.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterStatus('all');
    setCurrentPage(1);
  };

  if (loading && authors.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-solid border-purple-600 border-r-transparent"></div>
          <p className="mt-4 text-gray-600">Loading authors...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[--asfi-purple] flex items-center">
          <Users className="w-6 h-6 mr-3" />
          Authors
        </h1>
        <p className="text-gray-600 mt-1">Manage and view all registered authors</p>
      </div>

      {/* Stats Cards - Enhanced with more details */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Authors</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="w-10 h-10 bg-[--asfi-purple] bg-opacity-10 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
          </div>
          <div className="mt-2 flex justify-between text-xs">
            <span className="text-green-600">✓ {stats.verified} verified</span>
            <span className="text-orange-600">⚠ {stats.unverified} unverified</span>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Submissions</p>
              <p className="text-2xl font-bold text-blue-600">{stats.totalSubmissions}</p>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
          </div>
          <div className="mt-2 flex justify-between text-xs">
            <span className="text-green-600 flex items-center">
              <FileText className="w-3 h-3 mr-1" /> {stats.totalActiveSubmissions} active
            </span>
            <span className="text-gray-600 flex items-center">
              <Archive className="w-3 h-3 mr-1" /> {stats.totalArchivedSubmissions} archived
            </span>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Reviews</p>
              <p className="text-2xl font-bold text-purple-600">{stats.totalReviews}</p>
            </div>
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-purple-600" />
            </div>
          </div>
          <div className="mt-2 text-xs text-gray-600">
            Completed reviews across all authors
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Verification Rate</p>
              <p className="text-2xl font-bold text-green-600">
                {stats.total > 0 ? Math.round((stats.verified / stats.total) * 100) : 0}%
              </p>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
          </div>
          <div className="mt-2 text-xs text-gray-600">
            {stats.verified} of {stats.total} authors verified
          </div>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by name, email, affiliation, or location..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="verified">Verified</option>
              <option value="unverified">Unverified</option>
            </select>

            {/* View Toggle */}
            <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewType('table')}
                className={`p-2 rounded-md transition-all ${
                  viewType === 'table' 
                    ? 'bg-white shadow-sm text-[--asfi-purple]' 
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                }`}
                title="Table View"
              >
                <List className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewType('grid')}
                className={`p-2 rounded-md transition-all ${
                  viewType === 'grid' 
                    ? 'bg-white shadow-sm text-[--asfi-purple]' 
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                }`}
                title="Grid View"
              >
                <Grid className="w-4 h-4" />
              </button>
            </div>

            {/* Refresh Button */}
            <button
              onClick={clearFilters}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              title="Reset Filters"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Results Info */}
        <div className="mt-4 flex items-center justify-between text-sm">
          <p className="text-gray-600">
            Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, totalItems)}</span> of{' '}
            <span className="font-medium">{totalItems}</span> authors
          </p>
          {(searchTerm || filterStatus !== 'all') && (
            <button
              onClick={clearFilters}
              className="text-[--asfi-purple] hover:text-[--asfi-blue] text-sm font-medium"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Authors Display */}
      {authors.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-700 mb-2">No authors found</h3>
          <p className="text-gray-500">
            {searchTerm || filterStatus !== 'all' 
              ? 'Try adjusting your search or filters' 
              : 'No authors have registered yet.'}
          </p>
        </div>
      ) : viewType === 'table' ? (
        /* Table View */
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-[--asfi-blue]">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                    Author
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                    Affiliation
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                    Stats
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {authors.map((author) => {
                  const StatusBadge = getStatusBadge(author.status);
                  const StatusIcon = StatusBadge.icon;
                  const RoleBadge = getRoleBadge(author.role);
                  const RoleIcon = RoleBadge.icon;
                  
                  return (
                    <tr key={author.email} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-gradient-to-br from-[--asfi-blue] to-[--asfi-purple] rounded-lg flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-bold">
                              {author.firstName.charAt(0)}{author.lastName.charAt(0)}
                            </span>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {author.fullName}
                            </div>
                            <div className="text-xs text-gray-500 flex items-center">
                              <Mail className="w-3 h-3 mr-1" />
                              {author.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{author.affiliation}</div>
                        <div className="text-xs text-gray-500 flex items-center mt-1">
                          <MapPin className="w-3 h-3 mr-1" />
                          {author.city}, {author.country}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {author.date}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${StatusBadge.color}`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {author.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${RoleBadge.color}`}>
                          <RoleIcon className="w-3 h-3 mr-1" />
                          {author.role}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-xs space-y-1">
                          <div className="flex items-center text-blue-600">
                            <FileText className="w-3 h-3 mr-1" />
                            <span className="font-medium">{author.submissions}</span> total
                          </div>
                          <div className="flex items-center text-gray-500 text-xs ml-4">
                            <span className="text-green-600">{author.activeSubmissions || 0}</span> active · 
                            <span className="text-gray-600 ml-1">{author.archivedSubmissions || 0}</span> archived
                          </div>
                          <div className="flex items-center text-purple-600">
                            <Clock className="w-3 h-3 mr-1" />
                            <span className="font-medium">{author.reviews}</span> reviews
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleViewAuthor(author)}
                            className="p-2 text-gray-600 hover:text-white hover:bg-[--asfi-purple] hover:bg-opacity-10 rounded-lg transition-colors"
                            title="View Profile"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          
                          {author.status !== 'verified' && (
                            <button
                              onClick={() => handleVerifyClick(author)}
                              className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors"
                              title="Verify Account"
                            >
                              <UserCheck className="w-4 h-4" />
                            </button>
                          )}
                          
                          <button
                            onClick={() => handleMigrateClick(author)}
                            className="p-2 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-colors"
                            title="Migrate to Editor"
                          >
                            <UserCog className="w-4 h-4" />
                          </button>
                          
                          <button
                            onClick={() => handleDeleteClick(author)}
                            className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete Account"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Grid View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {authors.map((author) => {
            const StatusBadge = getStatusBadge(author.status);
            const StatusIcon = StatusBadge.icon;
            const RoleBadge = getRoleBadge(author.role);
            const RoleIcon = RoleBadge.icon;
            
            return (
              <div
                key={author.email}
                className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-gradient-to-br from-[--asfi-blue] to-[--asfi-purple] rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
                        <span className="text-white font-bold text-lg">
                          {author.firstName.charAt(0)}{author.lastName.charAt(0)}
                        </span>
                      </div>
                      <div className="ml-3">
                        <h3 className="font-semibold text-gray-900">
                          {author.fullName}
                        </h3>
                        <p className="text-xs text-gray-500">{author.email}</p>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${StatusBadge.color}`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {author.status}
                      </span>
                      <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${RoleBadge.color}`}>
                        <RoleIcon className="w-3 h-3 mr-1" />
                        {author.role}
                      </span>
                    </div>
                  </div>

                  {/* Affiliation */}
                  <div className="mb-4">
                    <p className="text-sm text-gray-700 line-clamp-2">{author.affiliation}</p>
                    <p className="text-xs text-gray-500 flex items-center mt-1">
                      <MapPin className="w-3 h-3 mr-1" />
                      {author.city}, {author.country}
                    </p>
                  </div>

                  {/* Stats - Enhanced with more details */}
                  <div className="space-y-2 mb-4">
                    <div className="bg-blue-50 rounded-lg p-2">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-blue-700 flex items-center">
                          <FileText className="w-3 h-3 mr-1" />
                          Submissions
                        </span>
                        <span className="text-sm font-bold text-blue-700">{author.submissions}</span>
                      </div>
                      <div className="flex justify-between text-xs text-gray-600 mt-1">
                        <span>Active: {author.activeSubmissions || 0}</span>
                        <span>Archived: {author.archivedSubmissions || 0}</span>
                      </div>
                    </div>
                    
                    <div className="bg-purple-50 rounded-lg p-2">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-purple-700 flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          Reviews
                        </span>
                        <span className="text-sm font-bold text-purple-700">{author.reviews}</span>
                      </div>
                    </div>
                  </div>

                  {/* Date */}
                  <div className="flex items-center text-xs text-gray-500 mb-4">
                    <Calendar className="w-3 h-3 mr-1" />
                    Registered: {author.date}
                  </div>

                  {/* ORCID if available */}
                  {author.orcid && (
                    <div className="mb-4 text-xs text-gray-500">
                      <span className="font-medium">ORCID:</span> {author.orcid}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleViewAuthor(author)}
                      className="flex-1 px-3 py-2 bg-[--asfi-purple] text-white text-sm font-medium rounded-lg hover:bg-[--asfi-blue] transition-colors flex items-center justify-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      View
                    </button>
                    
                    {author.status !== 'verified' && (
                      <button
                        onClick={() => handleVerifyClick(author)}
                        className="p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors"
                        title="Verify Account"
                      >
                        <UserCheck className="w-4 h-4" />
                      </button>
                    )}
                    
                    <button
                      onClick={() => handleMigrateClick(author)}
                      className="p-2 bg-purple-100 text-purple-600 rounded-lg hover:bg-purple-200 transition-colors"
                      title="Migrate to Editor"
                    >
                      <UserCog className="w-4 h-4" />
                    </button>
                    
                    <button
                      onClick={() => handleDeleteClick(author)}
                      className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                      title="Delete Account"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* View Author Modal */}
      {showViewModal && selectedAuthor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Author Profile</h3>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setSelectedAuthor(null);
                  }}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Basic Info */}
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-[--asfi-blue] to-[--asfi-purple] rounded-xl flex items-center justify-center shadow-lg">
                    <span className="text-white font-bold text-2xl">
                      {selectedAuthor.firstName.charAt(0)}{selectedAuthor.lastName.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {selectedAuthor.fullName}
                    </h2>
                    <p className="text-sm text-gray-600 flex items-center mt-1">
                      <Mail className="w-4 h-4 mr-2 text-gray-400" />
                      {selectedAuthor.email}
                    </p>
                  </div>
                </div>

                {/* Status and Role Badges */}
                <div className="flex gap-2">
                  {selectedAuthor.status === 'verified' ? (
                    <span className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-full border bg-green-100 text-green-800 border-green-200">
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Verified Author
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-full border bg-orange-100 text-orange-800 border-orange-200">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      Unverified Author
                    </span>
                  )}
                  
                  <span className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-full border ${
                    selectedAuthor.role === 'editor' 
                      ? 'bg-green-100 text-green-800 border-green-200' 
                      : selectedAuthor.role === 'reviewer'
                      ? 'bg-purple-100 text-purple-800 border-purple-200'
                      : 'bg-blue-100 text-blue-800 border-blue-200'
                  }`}>
                    {selectedAuthor.role === 'editor' ? (
                      <Shield className="w-4 h-4 mr-1" />
                    ) : selectedAuthor.role === 'reviewer' ? (
                      <UserCheck className="w-4 h-4 mr-1" />
                    ) : (
                      <User className="w-4 h-4 mr-1" />
                    )}
                    {selectedAuthor.role.charAt(0).toUpperCase() + selectedAuthor.role.slice(1)}
                  </span>
                </div>

                {/* Affiliation */}
                <div className="bg-purple-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Affiliation</h4>
                  <p className="text-gray-900">{selectedAuthor.affiliation}</p>
                  <p className="text-sm text-gray-600 flex items-center mt-2">
                    <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                    {selectedAuthor.city}, {selectedAuthor.country}
                  </p>
                </div>

                {/* ORCID */}
                {selectedAuthor.orcid && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">ORCID</h4>
                    <p className="text-sm text-gray-600">{selectedAuthor.orcid}</p>
                  </div>
                )}

                {/* Bio */}
                {selectedAuthor.bio && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Biography</h4>
                    <p className="text-sm text-gray-600">{selectedAuthor.bio}</p>
                  </div>
                )}

                {/* Stats Grid - Enhanced */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <p className="text-sm text-gray-500">Total Submissions</p>
                    <p className="text-2xl font-bold text-[--asfi-purple]">{selectedAuthor.submissions}</p>
                    <div className="flex justify-center gap-2 mt-2 text-xs">
                      <span className="text-green-600">{selectedAuthor.activeSubmissions || 0} active</span>
                      <span className="text-gray-500">•</span>
                      <span className="text-gray-600">{selectedAuthor.archivedSubmissions || 0} archived</span>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <p className="text-sm text-gray-500">Reviews Completed</p>
                    <p className="text-2xl font-bold text-[--asfi-purple]">{selectedAuthor.reviews}</p>
                  </div>
                </div>

                {/* Registration Date */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Registration Date</h4>
                  <p className="text-sm text-gray-600 flex items-center">
                    <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                    {selectedAuthor.date}
                  </p>
                </div>

                {/* ASFI Membership ID */}
                {selectedAuthor.asfiMembershipId && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">ASFI Membership ID</h4>
                    <p className="text-sm text-gray-600">{selectedAuthor.asfiMembershipId}</p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="mt-8 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setSelectedAuthor(null);
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                {selectedAuthor.status !== 'verified' && (
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      handleVerifyClick(selectedAuthor);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                  >
                    <UserCheck className="w-4 h-4" />
                    Verify Account
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    handleMigrateClick(selectedAuthor);
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                >
                  <UserCog className="w-4 h-4" />
                  Migrate to Editor
                </button>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    handleDeleteClick(selectedAuthor);
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Account
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Verify Account Modal */}
      {showVerifyModal && selectedAuthor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <UserCheck className="w-6 h-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">Verify Author Account</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Are you sure you want to verify this author?
                  </p>
                </div>
              </div>

              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700">Author:</p>
                <p className="text-sm text-gray-900 mt-1">
                  {selectedAuthor.fullName}
                </p>
                <p className="text-sm text-gray-600 mt-1">{selectedAuthor.email}</p>
                <div className="mt-2 flex gap-4 text-xs">
                  <span className="text-blue-600">{selectedAuthor.submissions} submissions</span>
                  <span className="text-purple-600">{selectedAuthor.reviews} reviews</span>
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="verifyReason" className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for Verification (Optional)
                </label>
                <textarea
                  id="verifyReason"
                  rows="3"
                  value={verifyReason}
                  onChange={(e) => setVerifyReason(e.target.value)}
                  placeholder="Enter any notes about this verification..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800 flex items-start">
                  <AlertTriangle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Note:</strong> Verified authors will be able to submit manuscripts and participate in the review process.
                  </span>
                </p>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowVerifyModal(false);
                    setSelectedAuthor(null);
                    setVerifyReason('');
                  }}
                  disabled={actionLoading}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleVerifyConfirm}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Verifying...
                    </>
                  ) : (
                    <>
                      <UserCheck className="w-4 h-4" />
                      Verify Account
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Migrate to Editor Modal */}
      {showMigrateModal && selectedAuthor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <UserCog className="w-6 h-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">Migrate to Editor</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    This will grant this user editor privileges.
                  </p>
                </div>
              </div>

              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700">Author:</p>
                <p className="text-sm text-gray-900 mt-1">
                  {selectedAuthor.fullName}
                </p>
                <p className="text-sm text-gray-600 mt-1">{selectedAuthor.email}</p>
                <p className="text-sm text-gray-600 mt-1">Current Role: {selectedAuthor.role}</p>
                <p className="text-sm text-gray-600 mt-1">Status: {selectedAuthor.status}</p>
                <div className="mt-2 flex gap-4 text-xs">
                  <span className="text-blue-600">{selectedAuthor.submissions} submissions</span>
                  <span className="text-purple-600">{selectedAuthor.reviews} reviews</span>
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="migrateRole" className="block text-sm font-medium text-gray-700 mb-1">
                  New Role
                </label>
                <select
                  id="migrateRole"
                  value={migrateRole}
                  onChange={(e) => setMigrateRole(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="editor">Editor</option>
                  <option value="reviewer">Reviewer</option>
                  <option value="author">Author (Revert)</option>
                </select>
              </div>

              <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <p className="text-sm text-purple-800 flex items-start">
                  <Shield className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Note:</strong> Editors have access to the admin panel and can manage submissions, reviewers, and other authors.
                  </span>
                </p>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowMigrateModal(false);
                    setSelectedAuthor(null);
                  }}
                  disabled={actionLoading}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleMigrateConfirm}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Migrating...
                    </>
                  ) : (
                    <>
                      <UserCog className="w-4 h-4" />
                      Migrate to {migrateRole}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeleteModal && selectedAuthor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">Delete Author Account</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    This action cannot be undone.
                  </p>
                </div>
              </div>

              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700">Author:</p>
                <p className="text-sm text-gray-900 mt-1">
                  {selectedAuthor.fullName}
                </p>
                <p className="text-sm text-gray-600 mt-1">{selectedAuthor.email}</p>
                <p className="text-sm text-gray-600 mt-1">Status: {selectedAuthor.status}</p>
                <p className="text-sm text-gray-600 mt-1">Role: {selectedAuthor.role}</p>
                <div className="mt-2 space-y-1 text-xs">
                  <p className="text-blue-600">Submissions: {selectedAuthor.submissions} total ({selectedAuthor.activeSubmissions || 0} active, {selectedAuthor.archivedSubmissions || 0} archived)</p>
                  <p className="text-purple-600">Reviews: {selectedAuthor.reviews}</p>
                </div>
              </div>

              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800 flex items-start">
                  <AlertTriangle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Warning:</strong> This will permanently delete this author's account and all associated data. This action cannot be reversed.
                  </span>
                </p>
              </div>

              <div className="mb-4">
                <label htmlFor="deleteConfirm" className="block text-sm font-medium text-gray-700 mb-1">
                  Type <span className="font-mono bg-gray-100 px-1 py-0.5 rounded">{selectedAuthor.email}</span> to confirm
                </label>
                <input
                  type="text"
                  id="deleteConfirm"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Enter email to confirm"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedAuthor(null);
                    setDeleteConfirmText('');
                  }}
                  disabled={actionLoading}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={actionLoading || deleteConfirmText !== selectedAuthor.email}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Permanently Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="footer text-center py-4 border-t border-gray-200 mt-8">
        <p className="text-sm text-gray-500">
          {new Date().getFullYear()} © ASFI - all rights reserved
        </p>
      </footer>
    </div>
  );
};

export default Authors;